import React, { useEffect, useState } from "react";
import StatsCard from "../../../components/Cards/StatsCard";
import LineChart from "../../../components/Charts/LineChart";
import PieChart from "../../../components/Charts/PieChart";
import APITable from "../../../components/Tables/APITable";
import DateRangePicker from "../../../components/Calender/DateRangePicker";
import Pagination from "../../../components/Pagination/Pagination";
import ProductUsagePanel from "../../../components/AdminComponents/ProductUsagePanel/ProductUsagePanel";
import { apiGet } from "../../../apiService";
import { can } from "../../../utils/permissions";
import {
  LineSkeleton,
  PieSkeleton,
  StatsSkeleton,
  TableSkeleton,
} from "../../../components/Skeleton/StatsSkeleton.jsx";
import {
  Users,
  ShieldCheck,
  Receipt,
  Wallet,
  Clock,
  Activity,
  Banknote,
  TrendingUp,
  TrendingDown,
  PieChart as PieChartIcon,
  Download,
  LayoutDashboard,
} from "lucide-react";

/* =======================
   Styles
======================= */
import styles from "./AdminDashboard.module.scss";

const AdminDashboard = () => {
  /* ===================== TAB STATE ===================== */
  // Default to the first dashboard tab the role is allowed to see.
  const [activeTab, setActiveTab] = useState(() =>
    can("dashboard", "product-dashboard") ? "product" : "api",
  );

  /* ===================== STATS STATE ===================== */
  const [stats, setStats] = useState({
    revenue: 0,
    growth: 0,
    pendingCount: 0,
    pendingAmount: 0,
    pendingRevenue: 0,
    pendingInvoice: 0,
  });

  const [Userapi, setUserapi] = useState({
    ClientValue: 0,
    ClientCount: 0,
    UserValue: 0,
    UserCount: 0,
  });

  /* ===================== FILTER & TAB STATES ===================== */
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statsData, setStatsData] = useState([]);
  const [appliedSearch] = useState("");
  const [errorCode] = useState("");
  const [datePickerKey, setDatePickerKey] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);
  const [lineGraphData, setLineGraphData] = useState([]);
  const [pieGraphData, setPieGraphData] = useState([]);
  const [graphLoading, setGraphLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
  });

  /* ===================== DASHBOARD STATS API ===================== */
  const fetchBillingDashboard = async () => {
    setStatsLoading(true);
    try {
      const res = await apiGet("billing", "/pay-dashboard");

      if (res?.success) {
        setStats({
          revenue: res.billingData.current_revenue,
          growth: res.billingData.growth_rate,
          pendingCount: res.billingData.pending_invoices_count,
          pendingAmount: res.billingData.pending_amount,
          pendingRevenue: res.billingData.total_pending_revenue_improve,
          pendingInvoice: res.billingData.total_pending_invoice_improve,
        });
      }
    } catch (err) {
      console.error("Billing dashboard error", err);
    } finally {
      setStatsLoading(false);
    }
  };

  /*==================== Admin User =====================*/
  const fetchAdminUserData = async () => {
    try {
      const res = await apiGet("user", "/client-detail-dashboard");
      if (res?.status === "success" && res?.data) {
        setUserapi({
          ClientValue: res.data.total_clients ?? 0,
          ClientCount: res.data.last_30_day_client_count ?? 0,
          UserValue: res.data.total_users ?? 0,
          UserCount: res.data.last_30_day_user_count ?? 0,
        });
      }
    } catch (error) {
      console.error("Admin user API error:", error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const res = await apiGet("catalog", "/api-usage-dashboard");

      if (res?.success && res.currentData?.length) {
        const current = res.currentData[0];
        const improvement = res.improvementData || {};

        setStatsData([
          {
            title: "Total API Hits",
            value: current.total_api_call ?? 0,
            icon: <Activity size={24} />,
            validationMsg: `${improvement.api_call_improvement ?? 0}% from last 30 days`,
          },
          {
            title: "Success Rate",
            value: `${current.success_rate ?? 0}%`,
            icon: <TrendingUp size={24} />,
            validationMsg: `${improvement.success_rate_improvement ?? 0}% from last 30 days`,
          },
          {
            title: "Error Rate",
            value: `${current.error_rate ?? 0}%`,
            icon: <TrendingDown size={24} />,
            negative: true,
            validationMsg: `${improvement.error_rate_improvement ?? 0}% from last 30 days`,
          },
          {
            title: "Average Response Time",
            value: `${current.average_latency_ms ?? 0} ms`,
            icon: <Clock size={24} />,
            validationMsg: `${improvement.average_latency_ms_improvement ?? 0} ms from last 30 days`,
          },
        ]);
      }
    } catch (error) {
      console.error("Dashboard stats API error", error);
    }
  };

  const resetDateFilter = () => {
    setFromDate("");
    setToDate("");
    setPage(1);
    setDatePickerKey((prev) => prev + 1);
  };

  /* ===================== DASHBOARD API ===================== */
  const fetchTableData = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
      };

      if (fromDate) {
        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);
        params.from_date = from.toISOString();
      }

      if (toDate) {
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        params.to_date = to.toISOString();
      }

      const query = new URLSearchParams(params).toString();
      const endpoint = `/api-usage?${query}`;
      const res = await apiGet("catalog", endpoint);

      if (res?.success) {
        setRows(res.data || []);
        setPagination({
          total: res.pagination.total,
          totalPages: res.pagination.totalPages,
        });
      }
    } catch (err) {
      console.error("Table API error", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGraphData = async () => {
    setGraphLoading(true);
    try {
      const res = await apiGet("catalog", "/api-usage-graph");
      setLineGraphData(res?.data?.lineGraphData || []);
      setPieGraphData(res?.data?.pieGraphData || []);
    } catch (err) {
      console.error("Graph API error", err);
    } finally {
      setGraphLoading(false);
    }
  };

  /* ===================== EFFECTS ===================== */
  /* Tab-scoped loading: the Product tab's APIs live inside ProductUsagePanel
     (fired when the panel mounts). The API tab's endpoints are fetched only
     when that tab is first activated, instead of all at once on page load. */
  const [apiTabLoaded, setApiTabLoaded] = useState(false);

  useEffect(() => {
    resetDateFilter();
  }, []);

  useEffect(() => {
    if (activeTab !== "api" || apiTabLoaded) return;
    setApiTabLoaded(true);
    fetchAdminUserData();
    fetchBillingDashboard();
    fetchDashboardStats();
    fetchGraphData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, apiTabLoaded]);

  useEffect(() => {
    if (activeTab !== "api") return;
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, limit, errorCode, fromDate, toDate, appliedSearch]);

  useEffect(() => {
    setPage(1);
  }, [errorCode, fromDate, toDate]);

  const columns = [
    { label: "Organisation", key: "company_name" },
    { label: "Category", key: "category" },
    { label: "API Name", key: "api_name" },
    { label: "Total Hits", key: "total_hits" },
    { label: "Error Rates (%)", key: "error_rate" },
    { label: "Average Response Time (SEC)", key: "avg_response_time_sec" },
  ];

  /* ===================== Download (csv) ===================== */
  const handleDownload = () => {
    const cols = columns;
    const header = cols.map((c) => c.label).join(",") + "\n";
    const body = rows
      .map((r) => cols.map((c) => r[c.key] ?? "").join(","))
      .join("\n");

    const blob = new Blob([header + body], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "api_usage.Admin.dashboard.csv";
    link.click();
  };

  return (
    <div className={styles["admin-dashboard__page"]}>
      <div className={styles["admin-dashboard__container"]}>
        {/* Header */}
        <div className={styles["admin-dashboard__header"]}>
          <div className={styles["admin-dashboard__icon-wrapper"]}>
            <LayoutDashboard size={24} strokeWidth={2.5} />
          </div>
          <div className={styles["admin-dashboard__title-group"]}>
            <h2 className={styles["admin-dashboard__title"]}>Dashboard</h2>
            <p className={styles["admin-dashboard__subtitle"]}>
              Monitor your API usage and performance metrics
            </p>
          </div>
        </div>

        {/* TABS */}
        <div className={styles["admin-dashboard__tabs"]} role="tablist">
          {can("dashboard", "product-dashboard") && (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "product"}
              className={`${styles["admin-dashboard__tab"]} ${
                activeTab === "product"
                  ? styles["admin-dashboard__tab--active"]
                  : ""
              }`}
              onClick={() => setActiveTab("product")}
            >
              Product
            </button>
          )}
          {can("dashboard", "api-dashboard") && (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "api"}
              className={`${styles["admin-dashboard__tab"]} ${
                activeTab === "api"
                  ? styles["admin-dashboard__tab--active"]
                  : ""
              }`}
              onClick={() => setActiveTab("api")}
            >
              API
            </button>
          )}
        </div>

        {can("dashboard", "product-dashboard") &&
          activeTab === "product" && <ProductUsagePanel />}

        {/* Stats Grid */}
        {can("dashboard", "api-dashboard") && activeTab === "api" && (
          <div className={styles["admin-dashboard__stats-grid"]}>
            {statsLoading ? (
              [...Array(8)].map((_, i) => <StatsSkeleton key={i} />)
            ) : (
              <>
                <StatsCard
                  title="Total Clients"
                  value={Userapi.ClientValue}
                  percentage="+5%"
                  icon={<Users size={24} />}
                  validationMsg={`${Userapi.ClientCount} from last 30 days`}
                />
                <StatsCard
                  title="Internal Users"
                  value={Userapi.UserValue}
                  percentage="+10%"
                  icon={<ShieldCheck size={24} />}
                  validationMsg={`${Userapi.UserCount} from last 30 days`}
                />
                {statsData.slice(0, 2).map((item, index) => (
                  <StatsCard key={`mid-${index}`} {...item} />
                ))}
                <StatsCard
                  title="Total Revenue"
                  value={`₹${stats.revenue} `}
                  total
                  percentage="+12.5%"
                  icon={<Banknote size={24} />}
                  validationMsg={`${stats.growth}% from last 30 days`}
                />
                <StatsCard
                  title="Pending Bills"
                  value={stats.pendingCount}
                  percentage="+4%"
                  icon={<Receipt size={24} />}
                  validationMsg={`${stats.pendingInvoice} from last 30 days`}
                />
                <StatsCard
                  title="Pending Amount"
                  value={stats.pendingAmount}
                  percentage="+4%"
                  negative
                  icon={<Wallet size={24} />}
                  validationMsg={`${stats.pendingRevenue} from last 30 days`}
                />
                {statsData[3] && <StatsCard {...statsData[3]} />}
              </>
            )}
          </div>
        )}
      </div>

      {can("dashboard", "api-dashboard") && activeTab === "api" && (
        <div className={styles["admin-dashboard__container"]}>
          {/* Charts Section */}
          {(graphLoading ||
            lineGraphData.length > 0 ||
            pieGraphData.length > 0) && (
            <div className={styles["admin-dashboard__charts-grid"]}>
              {(graphLoading || lineGraphData.length > 0) && (
                <div className={styles["admin-dashboard__chart-card"]}>
                  <div className={styles["admin-dashboard__chart-header"]}>
                    <div>
                      <h3 className={styles["admin-dashboard__chart-title"]}>
                        <span className={styles["admin-dashboard__chart-icon"]}>
                          <Activity size={20} />
                        </span>
                        API Usage Trend
                      </h3>
                      <p className={styles["admin-dashboard__chart-subtitle"]}>
                        Monthly API calls over time
                      </p>
                    </div>
                    <button className={styles["admin-dashboard__filter-btn"]}>
                      Last 6 Months
                    </button>
                  </div>
                  {graphLoading ? (
                    <LineSkeleton height={300} />
                  ) : (
                    <LineChart lineGraphData={lineGraphData} />
                  )}
                </div>
              )}

              {(graphLoading || pieGraphData.length > 0) && (
                <div className={styles["admin-dashboard__chart-card"]}>
                  <div className={styles["admin-dashboard__chart-header"]}>
                    <div>
                      <h3 className={styles["admin-dashboard__chart-title"]}>
                        <span className={styles["admin-dashboard__chart-icon"]}>
                          <PieChartIcon size={20} />
                        </span>
                        Category-wise Usage
                      </h3>
                      <p className={styles["admin-dashboard__chart-subtitle"]}>
                        Distribution by API category
                      </p>
                    </div>
                  </div>
                  {graphLoading ? (
                    <PieSkeleton />
                  ) : (
                    <PieChart pieGraphData={pieGraphData} />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Table Section */}
          {(loading || rows.length > 0) && (
            <div className={styles["admin-dashboard__table-card"]}>
              <div className={styles["admin-dashboard__table-header"]}>
                <h3 className={styles["admin-dashboard__table-title"]}>
                  API Requests
                </h3>

                <div className={styles["admin-dashboard__table-controls"]}>
                  <div className={styles["admin-dashboard__showing-text"]}>
                    Showing {rows.length} of {pagination.total}
                  </div>

                  <div className={styles["admin-dashboard__actions"]}>
                    <DateRangePicker
                      key={datePickerKey}
                      maxDate={new Date()}
                      onChange={(type, value) => {
                        if (type === "from") {
                          setFromDate(value);
                          setPage(1);
                        }
                        if (type === "to") {
                          setToDate(value);
                          setPage(1);
                        }
                      }}
                    />
                    {can("dashboard", "export-api-logs") && (
                      <button
                        className={styles["admin-dashboard__download-btn"]}
                        onClick={handleDownload}
                      >
                        <Download size={18} />
                        Download
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {loading ? (
                <TableSkeleton columns={6} rows={6} />
              ) : (
                <>
                  <APITable columns={columns} data={rows} />

                  <Pagination
                    currentPage={page}
                    totalPages={pagination.totalPages}
                    rowsPerPage={limit}
                    onPageChange={setPage}
                    onRowsPerPageChange={(l) => {
                      setLimit(l);
                      setPage(1);
                    }}
                  />
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
